/**
 * HealthThermometer — synthesises all pace projections into a single
 * health score (0–100) with a visual thermometer bar.
 */

import { useMemo, useState } from 'react'
import { HelpCircle, Heart, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import type { TrendResult } from '@/lib/services/trend-analysis.service'
import { SensitiveValue } from '@/components/privacy/SensitiveText'

/** Compact R$ formatter for the thermometer: R$ 1,2M / R$ 350K / R$ 900 */
function fmtCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000) return `${value < 0 ? '-' : ''}R$ ${(abs / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`
  if (abs >= 1_000) return `${value < 0 ? '-' : ''}R$ ${(abs / 1_000).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}K`
  return `${value < 0 ? '-' : ''}R$ ${abs.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

// ---------------------------------------------------------------------------
// Weight definitions
// ---------------------------------------------------------------------------

interface WeightedMetric {
  key: string
  label: string
  weight: number
  /** For cost metrics the score is inverted: being under PROA is good */
  invertForCost?: boolean
  /** Label suffix for the % value (default: "PROA") */
  refLabel?: string
}

// Weights reflect each metric's impact on Lucro Líquido.
// Revenue-side feeds profit; cost-side consumes it; Lucro is the final result.
const METRICS: WeightedMetric[] = [
  // Faturamento: displayed but not weighted (no PROA baseline)
  { key: 'faturamento', label: 'Faturamento', weight: 0 },
  // Revenue drivers (45%)
  { key: 'receita', label: 'Receita Bruta', weight: 0.37 },
  { key: 'receitaFinanceira', label: 'Receita Financeira', weight: 0.08 },
  // Cost drivers (45%, inverted — under budget is good)
  { key: 'folha', label: 'Folha', weight: 0.12, invertForCost: true },
  { key: 'terceiros', label: 'Terceiros Ajustado', weight: 0.21, invertForCost: true },
  { key: 'despesas', label: 'Despesas', weight: 0.04, invertForCost: true },
  { key: 'impostos', label: 'Impostos', weight: 0.08, invertForCost: true },
  // Bottom line (10%)
  { key: 'lucro', label: 'Lucro Ajustado', weight: 0.10 },
]

// ---------------------------------------------------------------------------
// Score helpers
// ---------------------------------------------------------------------------

function metricScore(trend: TrendResult | null | undefined, invertForCost = false): number {
  if (!trend) return 50 // neutral when data is missing
  let pct = trend.projectedPercent * 100
  if (invertForCost) {
    // For costs: 90% of PROA → score 110, 110% → score 90
    pct = 200 - pct
  }
  // Clamp to 0–100 so the final score never exceeds 100
  return Math.max(0, Math.min(100, pct))
}

function scoreColor(score: number): string {
  if (score >= 80) return 'text-brand-primary'
  if (score >= 60) return 'text-yellow-400'
  return 'text-red-400'
}

function scoreBgGradient(score: number): string {
  if (score >= 80) return 'from-brand-primary/80 to-brand-primary'
  if (score >= 60) return 'from-yellow-500/80 to-yellow-400'
  return 'from-red-500/80 to-red-400'
}

function scoreLabel(score: number): string {
  if (score >= 90) return 'Excelente'
  if (score >= 80) return 'Saudável'
  if (score >= 70) return 'Atenção'
  if (score >= 60) return 'Risco moderado'
  return 'Crítico'
}

function metricStatusIcon(pct: number, invertForCost: boolean) {
  const effective = invertForCost ? 200 - pct : pct
  if (effective >= 97) return { Icon: CheckCircle2, color: 'text-brand-primary' }
  if (effective >= 90) return { Icon: AlertTriangle, color: 'text-yellow-400' }
  return { Icon: XCircle, color: 'text-red-400' }
}

// ---------------------------------------------------------------------------
// Explanation Modal
// ---------------------------------------------------------------------------

function HealthExplanationModal({
  open,
  onOpenChange,
  trends,
  score,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  trends: Record<string, TrendResult | null | undefined>
  score: number
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg bg-brand-dark-light border-brand-dark-lighter text-white"
        overlayClassName="bg-black/80"
      >
        <DialogHeader>
          <DialogTitle className="text-white font-display text-lg flex items-center gap-2">
            <Heart size={18} className="text-brand-primary" />
            Como o score é calculado
          </DialogTitle>
          <DialogDescription className="text-brand-gray-light text-sm">
            Média ponderada das projeções de fim de ano vs PROA
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Weights table */}
          <div className="space-y-2">
            {METRICS.map((m) => {
              const trend = trends[m.key]
              const pct = trend ? Math.round(trend.projectedPercent * 100) : null
              const contribution = trend
                ? Math.round(metricScore(trend, m.invertForCost) * m.weight * 100) / 100
                : null
              return (
                <div key={m.key} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-brand-gray-light">{m.label}</span>
                    <span className="text-[10px] text-brand-gray bg-brand-dark-lighter px-1.5 py-0.5 rounded">
                      peso {(m.weight * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {pct !== null ? (
                      <>
                        <span className="text-brand-gray text-xs">
                          {pct}% {trend?.vsYear ? `vs ${trend.vsYear}` : 'do PROA'}
                        </span>
                        <span className="text-white font-semibold w-12 text-right">
                          {contribution?.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                        </span>
                      </>
                    ) : (
                      <span className="text-brand-gray text-xs">sem dados</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Divider + total */}
          <div className="border-t border-brand-dark-lighter pt-3 flex justify-between items-center">
            <span className="text-sm font-semibold text-white">Score total</span>
            <span className={`text-2xl font-bold font-display ${scoreColor(score)}`}>
              {score.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
            </span>
          </div>

          {/* Scale legend */}
          <div className="space-y-1 text-[11px]">
            <p className="text-brand-gray uppercase tracking-wider font-semibold">Faixas</p>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand-primary" />
              <span className="text-brand-gray-light">80–100 — Saudável</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-400" />
              <span className="text-brand-gray-light">60–79 — Atenção</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-400" />
              <span className="text-brand-gray-light">0–59 — Crítico</span>
            </div>
          </div>

          {/* Formula */}
          <div className="bg-brand-dark-lighter/50 rounded-lg p-3 text-[11px] text-brand-gray">
            <p className="font-semibold text-brand-gray-light mb-1">Fórmula</p>
            <p>Score = Σ (projeção_métrica × peso)</p>
            <p className="mt-1">Para custos (Folha, Terceiros), a projeção é invertida: gastar menos que o orçamento aumenta o score.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

interface HealthThermometerProps {
  faturamentoTrend?: TrendResult | null
  receitaTrend?: TrendResult | null
  receitaFinanceiraTrend?: TrendResult | null
  folhaTrend?: TrendResult | null
  terceirosTrend?: TrendResult | null
  despesasTrend?: TrendResult | null
  impostosTrend?: TrendResult | null
  lucroTrend?: TrendResult | null
  /** Period start (ISO YYYY-MM-DD) for dynamic title */
  startDate?: string
  /** Period end (ISO YYYY-MM-DD) for dynamic title */
  endDate?: string
}

/** Format ISO date to dd/mm/yy */
function fmtDateShort(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y.slice(-2)}`
}

const MONTH_NAMES_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

/**
 * Detect which period matches the current dates and return
 * the appropriate title line + whether it's the default YTD.
 */
function getPeriodLabel(startDate?: string, endDate?: string): { label: string; isYTD: boolean } {
  if (!startDate || !endDate) return { label: String(new Date().getFullYear()), isYTD: true }

  const today = new Date()
  const currentYear = today.getFullYear()

  const [sy, sm, sd] = startDate.split('-').map(Number)
  const [ey, em, ed] = endDate.split('-').map(Number)

  // Same year checks
  if (sy === ey) {
    // Full year: Jan 1 → Dec 31
    if (sm === 1 && sd === 1 && em === 12 && ed === 31) {
      return { label: String(sy), isYTD: sy === currentYear }
    }

    // Month range: starts on 1st, ends on last day of a month (check BEFORE YTD)
    if (sd === 1) {
      const lastDay = new Date(ey, em, 0).getDate()
      if (ed === lastDay) {
        const startMonth = MONTH_NAMES_SHORT[sm - 1]
        const endMonth = MONTH_NAMES_SHORT[em - 1]
        const yearSuffix = String(sy).slice(-2)
        if (sm === em) {
          // Single month
          return { label: `${startMonth}/${yearSuffix}`, isYTD: false }
        }
        // Month range
        return { label: `${startMonth}-${endMonth}/${yearSuffix}`, isYTD: false }
      }
    }

    // YTD: Jan 1 current year → any date in current year (not a clean month boundary)
    if (sy === currentYear && sm === 1 && sd === 1) {
      return { label: String(currentYear), isYTD: true }
    }
  }

  // Single day
  if (startDate === endDate) {
    return { label: fmtDateShort(startDate), isYTD: false }
  }

  // Custom range
  return { label: `${fmtDateShort(startDate)} a ${fmtDateShort(endDate)}`, isYTD: false }
}

export function HealthThermometer({
  faturamentoTrend,
  receitaTrend,
  receitaFinanceiraTrend,
  folhaTrend,
  terceirosTrend,
  despesasTrend,
  impostosTrend,
  lucroTrend,
  startDate,
  endDate,
}: HealthThermometerProps) {
  const [modalOpen, setModalOpen] = useState(false)

  const trends: Record<string, TrendResult | null | undefined> = {
    faturamento: faturamentoTrend,
    receita: receitaTrend,
    receitaFinanceira: receitaFinanceiraTrend,
    folha: folhaTrend,
    terceiros: terceirosTrend,
    despesas: despesasTrend,
    impostos: impostosTrend,
    lucro: lucroTrend,
  }

  const { score, metricDetails } = useMemo(() => {
    let total = 0
    const details = METRICS.map((m) => {
      const trend = trends[m.key]
      const raw = metricScore(trend, m.invertForCost)
      const contribution = raw * m.weight
      total += contribution
      const pct = trend ? Math.round(trend.projectedPercent * 100) : null
      return { ...m, pct, contribution: Math.round(contribution * 100) / 100, raw: Math.round(raw) }
    })
    return { score: Math.round(total * 100) / 100, metricDetails: details }
  }, [faturamentoTrend, receitaTrend, receitaFinanceiraTrend, folhaTrend, terceirosTrend, despesasTrend, impostosTrend, lucroTrend])

  const periodInfo = useMemo(() => getPeriodLabel(startDate, endDate), [startDate, endDate])

  return (
    <>
      <div className="py-3 space-y-3">
        {/* Title row */}
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-lg font-bold text-brand-primary font-display leading-tight">{periodInfo.label}</span>
            <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide">Monu Index</span>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="text-brand-gray hover:text-brand-primary transition-colors flex-shrink-0"
            aria-label="Como o score de saúde é calculado"
          >
            <HelpCircle size={14} strokeWidth={1.5} />
          </button>
        </div>

        {/* Score + bar */}
        <div className="flex items-center gap-4">
          <span className={`text-3xl font-bold font-display ${scoreColor(score)}`}>
            {score.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
          </span>
          <div className="flex-1 bg-brand-dark-lighter rounded-full h-2">
            <div
              className={`h-2 rounded-full bg-gradient-to-r ${scoreBgGradient(score)} transition-all duration-700 ease-out`}
              style={{ width: `${Math.min(100, score)}%` }}
            />
          </div>
        </div>

        {/* Metric indicators — table with YTD + Proj columns */}
        {(() => {
          const firstTrend = Object.values(trends).find(Boolean)
          const yearSuffix = firstTrend ? String(firstTrend.currentYear).slice(-2) : ''
          return (
            <div className="space-y-1">
              {/* Column headers */}
              <div className="flex items-center text-[9px] text-brand-gray/60 uppercase tracking-wider">
                <span className="flex-1">Métrica</span>
                <span className="w-16 text-right">{periodInfo.isYTD ? 'YTD' : 'Período'}</span>
                <span className="w-20 text-right">Proj. {yearSuffix}</span>
              </div>
              {metricDetails.map((m) => {
                if (m.pct === null) return null
                const { Icon, color } = metricStatusIcon(m.pct, !!m.invertForCost)
                const trend = trends[m.key]
                const ytd = trend ? fmtCompact(trend.realYTD) : '—'
                return (
                  <div key={m.key} className="flex items-center text-[10px]">
                    <div className="flex items-center gap-1 flex-1 min-w-0">
                      <Icon size={11} strokeWidth={1.5} className={color} />
                      <span className="text-brand-gray truncate">{m.label}</span>
                    </div>
                    <span className="w-16 text-right text-brand-gray-light">
                      <SensitiveValue mask="•••">{ytd}</SensitiveValue>
                    </span>
                    <span className="w-20 text-right text-brand-gray-light font-medium">
                      {m.pct}% {trend?.vsYear ? `vs ${trend.vsYear}` : 'PROA'}
                    </span>
                  </div>
                )
              })}
            </div>
          )
        })()}
      </div>

      <HealthExplanationModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        trends={trends}
        score={score}
      />
    </>
  )
}
